import { push } from 'connected-react-router'
import { all, call, put, take, takeEvery } from 'redux-saga/effects'
import { t } from 'ttag'
import { getWhoAmI, getWhoAmIFail, getWhoAmISuccess } from '../../../common/auth/authActions'
import { addNotification, clearNotification } from '../../../common/notifications/NotificationActions'
import { INotification } from '../../../common/notifications/NotificationReducers'
import { reLocate } from '../../../common/Routes/RouterActions'
import { ROUTES } from '../../../common/Routes/routePaths'
import api from '../../../services/api.services'
import {
  verifyEmailCode,
  verifyEmailCodeFail,
  verifyEmailCodeSuccess,
  resendVerificationCode,
  resendVerificationCodeFail,
  resendVerificationCodeSuccess,
} from './VerifyEmailActions'
import { currentLangString } from '../../../i18nInit'

/**
 * Verify the 6-digit code. The purpose determines what the back-end expects and
 * where we navigate on success.
 *
 * - 'LOGIN' or 'SIGNUP': call verifyEmailCode, then getWhoAmI, then reLocate (dashboard or register-user).
 * - 'PASSWORD_RESET': call verifyResetPasswordCode, then navigate to the reset-password screen.
 */
function* sagaVerifyCode({ payload }: any) {
  try {
    yield put({ type: String(clearNotification) })
    const { code, purpose } = payload

    if (purpose === 'PASSWORD_RESET') {
      yield call(api.auth.verifyResetPasswordCode, { code })
      // Refresh whoAmI so the frontend state picks up the cleared verification flag (the
      // reset-password flag stays true). Otherwise the LOCATION_CHANGE guard would bounce
      // the user back to /verify-email.
      yield put({ type: String(getWhoAmI) })
      yield take([String(getWhoAmISuccess), String(getWhoAmIFail)])
      yield put({ type: String(verifyEmailCodeSuccess) })
      yield put(push(ROUTES.RESET_PASSWORD.to))
      return
    }

    yield call(api.auth.verifyEmailCode, { code, purpose })
    // Refresh whoAmI so the auth state reflects the cleared verification flag.
    yield put({ type: String(getWhoAmI) })
    yield take([String(getWhoAmISuccess), String(getWhoAmIFail)])
    yield put({ type: String(verifyEmailCodeSuccess) })
    yield put({ type: String(reLocate) })
  } catch (e: any) {
    const message: string = (e && e.message) || ''
    yield put({ type: String(verifyEmailCodeFail), payload: message })
    let content = t`The code you entered is incorrect. Please try again.`
    if (message === 'expiredCode') {
      content = t`The code has expired. Please request a new one.`
    } else if (message === 'tooManyAttempts') {
      content = t`Too many failed attempts. Please request a new code.`
    }
    const notification: INotification = { type: 'warning', content }
    yield put({ type: String(addNotification), payload: notification })
  }
}

function* sagaResendCode({ payload }: any) {
  try {
    yield put({ type: String(clearNotification) })
    const { purpose } = payload

    if (purpose === 'PASSWORD_RESET') {
      // Re-issuing a reset-password code means re-starting the flow — caller must
      // supply an email via the forgot-password screen. Just navigate back.
      yield put(push(ROUTES.FORGOT_PASSWORD.to))
      yield put({ type: String(resendVerificationCodeSuccess) })
      return
    }

    yield call(api.auth.resendEmailVerificationCode, { purpose, lang: currentLangString })
    yield put({ type: String(resendVerificationCodeSuccess) })
    const notification: INotification = {
      type: 'success',
      content: t`A new code has been sent to your email.`,
    }
    yield put({ type: String(addNotification), payload: notification })
  } catch (e: any) {
    yield put({ type: String(resendVerificationCodeFail) })
    const message: string = (e && e.message) || ''
    let content = t`Could not send a new code. Please try again in a moment.`
    if (message.startsWith('tooSoon')) {
      const parts = message.split('|')
      const seconds = parts.length > 1 ? parseInt(parts[1], 10) : 60
      content = t`Please wait ${seconds}s before requesting a new code.`
    }
    const notification: INotification = { type: 'warning', content }
    yield put({ type: String(addNotification), payload: notification })
  }
}

export function* verifyEmailSagas() {
  yield all([
    takeEvery(String(verifyEmailCode), sagaVerifyCode),
    takeEvery(String(resendVerificationCode), sagaResendCode),
  ])
}
